import React, { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import type { WarningInfo } from '../types/api';

/**
 * Subtle amber panel that lists non-fatal sync warnings.
 *
 * Each warning shows its catalog title (or raw code if title is null —
 * defensive against stale codes). help_text and observed_at appear when
 * the panel is expanded. Collapsed by default so the user sees a one-line
 * "N warnings" advisory and chooses whether to dig in.
 *
 * Shared by the deal-detail page and the sync-preview dialog so a warning
 * reads the same before and after a sync. Both readers dedupe on
 * (code, context) server-side, so a failure hitting every line item arrives
 * here once — see backend/warning_catalog.py:warning_dedupe_key.
 */
const WarningsPanel: React.FC<{ warnings: WarningInfo[] }> = ({ warnings }) => {
  const [expanded, setExpanded] = useState(false);
  const count = warnings.length;
  return (
    <Alert
      className="mt-4 border-amber-400 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-700/50"
    >
      <Button
        type="button"
        variant="ghost"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        className="w-full justify-start px-0 h-auto font-normal hover:bg-transparent"
      >
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle className="flex-1 mb-0 text-left">
          {count === 1 ? '1 warning' : `${count} warnings`}
        </AlertTitle>
        {expanded
          ? <ChevronUp className="h-4 w-4" />
          : <ChevronDown className="h-4 w-4" />
        }
      </Button>
      {expanded && (
        <AlertDescription className="mt-3 space-y-3">
          {warnings.map((w, idx) => (
            <div key={`${w.code}-${idx}`} className="text-sm">
              <div className="font-semibold">{w.title ?? w.code}</div>
              {w.help_text && (
                <div className="mt-1 text-xs opacity-90">{w.help_text}</div>
              )}
              {w.observed_at && (
                <div className="mt-1 text-[10px] opacity-70">
                  Observed: {w.observed_at}
                </div>
              )}
            </div>
          ))}
        </AlertDescription>
      )}
    </Alert>
  );
};

export default WarningsPanel;
